// バックエンドの PasswordPolicy.cs と同じ規則を保つ (NIST SP 800-63B-4 準拠)。
// 文字種の要件は組織が有効にしたときだけ追加する。

export const PASSWORD_MIN_LENGTH = 15;
export const PASSWORD_MAX_LENGTH = 128;
const MIN_PERSONAL_TOKEN_LENGTH = 3;
const MIN_DISTINCT_CHARS = 5;
const SERVICE_NAME = "izanagi";

const KEYBOARD_SEQUENCES = [
	"qwertyuiopasdfghjklzxcvbnm",
	"qazwsxedcrfvtgbyhnujmikolp",
	"1q2w3e4r5t6y7u8i9o0p",
	"1qaz2wsx3edc4rfv5tgb6yhn7ujm8ik9ol0p",
].flatMap((seq) => [seq, [...seq].reverse().join("")]);

export interface PasswordRuleResult {
	label: string;
	/** 条件に当てはまる文字の一覧。ラベルの後ろにコード表記で出す。 */
	characters?: string;
	satisfied: boolean;
}

export interface PasswordCharacterRequirements {
	uppercase: boolean;
	lowercase: boolean;
	digit: boolean;
	symbol: boolean;
}

export const NO_CHARACTER_REQUIREMENTS: PasswordCharacterRequirements = {
	uppercase: false,
	lowercase: false,
	digit: false,
	symbol: false,
};

interface FixedPasswordRule {
	label: string;
	isSatisfied: (password: string, personalInfo: string[]) => boolean;
}

export const FIXED_PASSWORD_RULES: FixedPasswordRule[] = [
	{
		label: `${PASSWORD_MIN_LENGTH}〜${PASSWORD_MAX_LENGTH}文字`,
		isSatisfied: (password) =>
			password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
	},
	{
		label: `異なる文字を${MIN_DISTINCT_CHARS}つ以上使う`,
		isSatisfied: (password) => new Set(password.toLowerCase().split("")).size >= MIN_DISTINCT_CHARS,
	},
	{
		label: "同じ文字の繰り返し・連番・キーボードの並びではない（例: aaaa…, 1234…, qwerty…）",
		isSatisfied: (password) => password.length > 0 && !isSimplePattern(password),
	},
	{
		label: "メールアドレス・氏名・「Izanagi」を含まない",
		isSatisfied: (password, personalInfo) =>
			password.length > 0 && !containsPersonalInfo(password, personalInfo),
	},
];

/** 記号として数える文字 (ASCII の記号すべて)。 */
export const PASSWORD_SYMBOLS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

export const CHARACTER_CLASS_RULES: {
	key: keyof PasswordCharacterRequirements;
	label: string;
	characters?: string;
	isSatisfied: (password: string) => boolean;
}[] = [
	{ key: "uppercase", label: "英大文字 (A〜Z) を含む", isSatisfied: (p) => /[A-Z]/.test(p) },
	{ key: "lowercase", label: "英小文字 (a〜z) を含む", isSatisfied: (p) => /[a-z]/.test(p) },
	{ key: "digit", label: "数字 (0〜9) を含む", isSatisfied: (p) => /[0-9]/.test(p) },
	{
		key: "symbol",
		label: "次のいずれかの記号を含む",
		characters: PASSWORD_SYMBOLS,
		isSatisfied: (p) => [...p].some((c) => PASSWORD_SYMBOLS.includes(c)),
	},
];

export function evaluatePasswordRules(
	password: string,
	personalInfo: string[],
	requirements: PasswordCharacterRequirements,
): PasswordRuleResult[] {
	return [
		...FIXED_PASSWORD_RULES.map((rule) => ({
			label: rule.label,
			satisfied: rule.isSatisfied(password, personalInfo),
		})),
		...CHARACTER_CLASS_RULES.filter((rule) => requirements[rule.key]).map((rule) => ({
			label: rule.label,
			characters: rule.characters,
			satisfied: rule.isSatisfied(password),
		})),
	];
}

function isSimplePattern(password: string): boolean {
	const lower = password.toLowerCase();
	return (
		isRepeatedUnit(lower) ||
		isSequential(lower) ||
		KEYBOARD_SEQUENCES.some((seq) => seq.includes(lower))
	);
}

// 「abab…a」のように途中で切れる繰り返しも含める。
function isRepeatedUnit(s: string): boolean {
	for (let unit = 1; unit <= s.length / 2; unit++) {
		if (
			s ===
			s
				.slice(0, unit)
				.repeat(Math.ceil(s.length / unit))
				.slice(0, s.length)
		)
			return true;
	}
	return false;
}

function isSequential(s: string): boolean {
	if (s.length < 2) return false;
	const direction = step(s[0], s[1]);
	if (direction !== 1 && direction !== -1) return false;
	for (let i = 2; i < s.length; i++) {
		if (step(s[i - 1], s[i]) !== direction) return false;
	}
	return true;
}

// 「…7890123…」のように 9 の次の 0 も連番として扱う。
function step(a: string, b: string): number {
	const diff = b.charCodeAt(0) - a.charCodeAt(0);
	if (!/[0-9]/.test(a) || !/[0-9]/.test(b)) return diff;
	const wrapped = (diff + 10) % 10;
	if (wrapped === 1) return 1;
	if (wrapped === 9) return -1;
	return 0;
}

function containsPersonalInfo(password: string, personalInfo: string[]): boolean {
	const lower = password.toLowerCase();
	return personalTokens(personalInfo).some((token) => lower.includes(token));
}

function personalTokens(personalInfo: string[]): string[] {
	const tokens = [SERVICE_NAME];
	for (const info of personalInfo) {
		if (!info.trim()) continue;
		const localPart = info.split("@")[0];
		for (const token of [...localPart.split(/[^\p{L}\p{N}]+/u), localPart]) {
			if (token.length >= MIN_PERSONAL_TOKEN_LENGTH) tokens.push(token.toLowerCase());
		}
	}
	return tokens;
}
